/**
 * BrokerageKpiStrip — the four headline numbers at the top of the
 * Brokerage Hub overview: total brokerages, total leads (lifetime),
 * total converted (lifetime), and the average conversion rate.
 *
 * The values are computed in the parent (BrokerageHub.jsx) by
 * summing the per-brokerage stats, so they line up exactly with what
 * the drill shows when each tile is clicked. We don't use the
 * backend's MTD numbers here — pairing those with a lifetime-scoped
 * drill produced inconsistent counts (e.g. tile said 0 but drill
 * showed firms with lifetime wins). See the comment on derivedKpis
 * in BrokerageHub.jsx for the full rationale.
 *
 * Each tile is also a drill-down "tab" — click it to filter / sort
 * the brokerage list below by that metric:
 *
 *   Total Brokerages    → show all firms (default)
 *   Total Leads         → only firms with leads, sorted by lead count desc
 *   Total Converted     → only firms with wins, sorted by won count desc
 *   Avg Conversion Rate → only firms with leads, sorted by conv rate desc
 *
 * The selection lives in the parent so the cards below can read it.
 * We just render the visual state.
 */
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Building2, Users, CheckCircle2, TrendingUp } from 'lucide-react';

// Labels intentionally drop "(MTD)" — these tiles now show lifetime
// totals computed from the same per-brokerage stats the drill below
// uses, so the tile count and the drilled list count match 1:1.
// The keys ("leads_mtd", "converted_mtd") are kept for back-compat
// with the parent's KPI shape; we only changed the visible label.
const TILES = [
  {
    key: 'total_brokerages', label: 'Total Brokerages', icon: Building2,
    tone: 'text-violet-500', ring: 'ring-violet-300', bg: 'bg-violet-50',
    fmt: (v) => v ?? 0,
  },
  {
    key: 'leads_mtd', label: 'Total Leads', icon: Users,
    tone: 'text-blue-500', ring: 'ring-blue-300', bg: 'bg-blue-50',
    fmt: (v) => v ?? 0,
  },
  {
    key: 'converted_mtd', label: 'Total Converted', icon: CheckCircle2,
    tone: 'text-emerald-500', ring: 'ring-emerald-300', bg: 'bg-emerald-50',
    fmt: (v) => v ?? 0,
  },
  {
    key: 'avg_conversion_rate', label: 'Avg Conversion Rate', icon: TrendingUp,
    tone: 'text-amber-500', ring: 'ring-amber-300', bg: 'bg-amber-50',
    fmt: (v) => `${v ?? 0}%`,
  },
];

export default function BrokerageKpiStrip({
  kpis,
  loading,
  activeKey = 'total_brokerages',
  onSelect,
}) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {TILES.map(({ key, label, icon: Icon, tone, ring, bg, fmt }) => {
        const active = activeKey === key;
        // We render a real <button> so keyboard users can Tab + Enter
        // through the strip. Active state lifts the ring colour to
        // match the tile's tone, tints the background, and shows a
        // small DRILLED label so it's obvious which view the list
        // below is currently scoped to.
        return (
          <button
            key={key}
            type="button"
            onClick={() => onSelect?.(key)}
            aria-pressed={active}
            title={active ? 'Click to clear drill-down' : `Drill into ${label}`}
            className={`group block text-left transition focus:outline-none rounded-lg ${
              active ? `ring-2 ${ring}` : 'hover:ring-1 hover:ring-slate-300'
            }`}
          >
            <Card className={active ? bg : ''}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Icon size={14} className={tone} />
                  <span className="flex-1">{label}</span>
                  {active && (
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-violet-600">
                      drilled
                    </span>
                  )}
                </div>
                {loading ? (
                  <Skeleton className="h-8 w-20 mt-2" />
                ) : (
                  <div className="text-2xl font-bold mt-1">{fmt(kpis?.[key])}</div>
                )}
                {!active && (
                  // Subtle "click to drill" hint that only appears on
                  // hover so it doesn't compete with the headline
                  // number. Keeps the tile clean at rest.
                  <div className="mt-1 text-[10px] text-violet-500 opacity-0 group-hover:opacity-100">
                    click to filter list ↓
                  </div>
                )}
              </CardContent>
            </Card>
          </button>
        );
      })}
    </div>
  );
}
